// SpeedDatingLobbyPage.js
// Updated with InlineNotice for consistent error handling
import React, { useEffect, useState, useCallback, useContext } from "react";
import { useParams } from "react-router-dom";
import {
  fetchSpeedDatingEvent,
  likeSpeedDatingPartner,
} from "../api/speeddating";
import { fetchEvent } from "../api/events";
import { checkVideoEligibility } from "../api/video";
import RoundTimer from "../components/speeddating/RoundTimer";
import PartnerCard from "../components/speeddating/PartnerCard";
import MatchToast from "../components/speeddating/MatchToast";
import VideoDateButton from "../components/speeddating/VideoDateButton";
import MoreWaysToJoin from "../components/speeddating/MoreWaysToJoin";
import useSpeedDatingRealtime from "../hooks/useSpeedDatingRealtime";
import { AuthContext } from "../App";
import InlineNotice from "../components/common/InlineNotice";
import { useInlineNotice } from "../hooks/useInlineNotice";

export default function SpeedDatingLobbyPage() {
  const { eventId } = useParams();
  const { user, token } = useContext(AuthContext);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roundIndex, setRoundIndex] = useState(0);
  const [currentPartner, setCurrentPartner] = useState(null);
  const [roundStartedAt, setRoundStartedAt] = useState(null);
  const [likeInFlight, setLikeInFlight] = useState(false);
  const [matchToastVisible, setMatchToastVisible] = useState(false);
  const [matchPartnerName, setMatchPartnerName] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [isInnerCircle, setIsInnerCircle] = useState(false);

  // Use InlineNotice hook for consistent error handling
  const { notice, showError, showSuccess, showInfo, dismissNotice } = useInlineNotice("speed-dating", token);

  // Check verification status
  useEffect(() => {
    async function checkStatus() {
      try {
        const eligibility = await checkVideoEligibility();
        setIsVerified(eligibility.identity_verified);
      } catch (err) {
        console.error('[SpeedDating] Failed to check eligibility:', err);
        // Don't show error - this is a background check
      }
      
      // Check Inner Circle status from user entitlements
      if (user?.entitlements?.inner_circle?.active) {
        setIsInnerCircle(true);
      }
    }
    
    if (user?.id) {
      checkStatus();
    }
  }, [user?.id, user?.entitlements]);

  // Real-time event handlers
  const handleEventStarted = useCallback((data) => {
    console.log('[SpeedDating] Event started via realtime:', data);
    setRoundIndex(data.round_index ?? 0);
    showInfo("Round 1 is beginning...", { title: "Event started!" });
    setStatusMessage("Event started! Round 1 is beginning...");
  }, [showInfo]);

  const handleRoundStarted = useCallback((data) => {
    console.log('[SpeedDating] Round started via realtime:', data);
    setRoundIndex(data.round_index ?? 0);
    setCurrentPartner(data.partner ?? null);
    setRoundStartedAt(data.started_at ?? new Date().toISOString());
    dismissNotice(); // Clear any previous notice for the new round
    setStatusMessage("New round started – take a breath and say hi 👋");
  }, [dismissNotice]);

  const handleEventFinished = useCallback(() => {
    console.log('[SpeedDating] Event finished via realtime');
    showSuccess("Your mutual likes will appear in Matches.", { title: "Event finished!" });
    setStatusMessage("Event finished. Your mutual likes will appear in Matches.");
    setCurrentPartner(null);
    setRoundStartedAt(null);
    setEvent(prev => prev ? { ...prev, status: 'finished' } : prev);
  }, [showSuccess]);

  const handleMatchCreated = useCallback((data) => {
    console.log('[SpeedDating] Match created via realtime:', data);
    setMatchPartnerName(data.partner_name || "your partner");
    setMatchToastVisible(true);
  }, []);

  // Subscribe to real-time events
  useSpeedDatingRealtime(eventId, {
    onEventStarted: handleEventStarted,
    onRoundStarted: handleRoundStarted,
    onEventFinished: handleEventFinished,
    onMatchCreated: handleMatchCreated,
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      dismissNotice();
      
      // Try the new events API first (/api/events/{id}), fall back to legacy speed dating API
      let data;
      try {
        data = await fetchEvent(eventId, localStorage.getItem('token'));
      } catch {
        // Fallback to legacy speed dating API for older event IDs
        data = await fetchSpeedDatingEvent(eventId);
      }
      setEvent(data);

      if (data.current_round_index != null) {
        setRoundIndex(data.current_round_index);
      }

      if (data.current_partner) {
        setCurrentPartner(data.current_partner);
      } else {
        setCurrentPartner(null);
      }

      if (data.current_round_started_at) {
        setRoundStartedAt(data.current_round_started_at);
      } else {
        setRoundStartedAt(null);
      }

      setStatusMessage(
        data.status === "scheduled"
          ? "Waiting for the host to start the event."
          : data.status === "running"
          ? "You are in a live round. Be kind, be present."
          : data.status === "finished"
          ? "This event is finished. Check your matches!"
          : ""
      );
    } catch (err) {
      console.error(err);
      showError(err, { 
        customMessage: "Couldn't load the speed dating event. Please try again.",
        onRetry: load,
      });
    } finally {
      setLoading(false);
    }
  }, [eventId, dismissNotice, showError]);

  useEffect(() => {
    load();
  }, [load]);

  const handleLike = async () => {
    if (!event || !currentPartner || likeInFlight) return;
    setLikeInFlight(true);
    dismissNotice();
    try {
      const res = await likeSpeedDatingPartner(
        event.id,
        roundIndex,
        currentPartner.id
      );
      if (res?.mutual_match) {
        setMatchPartnerName(currentPartner.name || "your partner");
        setMatchToastVisible(true);
      }
      setStatusMessage("Choice saved. Focus on the connection, not the outcome.");
    } catch (err) {
      console.error(err);
      showError(err, { 
        customMessage: "Couldn't send your interest. Please try again.",
        onRetry: handleLike,
      });
    } finally {
      setLikeInFlight(false);
    }
  };

  const handlePass = async () => {
    if (!event || !currentPartner || likeInFlight) return;
    setLikeInFlight(true);
    dismissNotice();
    try {
      await likeSpeedDatingPartner(event.id, roundIndex, currentPartner.id, {
        decision: "pass",
      });
      setStatusMessage("Pass saved. Next round may bring a better match.");
    } catch (err) {
      console.error(err);
      showError(err, { 
        customMessage: "Couldn't save your choice. Please try again.",
        onRetry: handlePass,
      });
    } finally {
      setLikeInFlight(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-black via-slate-950 to-black text-white">
        <p className="text-white/70">Loading event…</p>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-black via-slate-950 to-black text-white px-4">
        <div className="w-full max-w-md text-center">
          {notice ? (
            <InlineNotice
              variant={notice.variant}
              title={notice.title}
              actionLabel={notice.actionLabel}
              onAction={notice.onAction}
              href={notice.href}
              testId="speed-dating-notice"
            >
              {notice.message}
            </InlineNotice>
          ) : (
            <>
              <div className="text-5xl mb-4">⚡</div>
              <h2 className="text-xl font-semibold text-white mb-2">Event not found</h2>
              <p className="text-white/60 mb-6">
                This event may have ended, been cancelled, or the link is incorrect.
              </p>
            </>
          )}
          <button
            onClick={() => window.location.href = '/events/speed-dating'}
            className="mt-4 px-6 py-2 rounded-full bg-pink-600 hover:bg-pink-700 text-white font-medium transition"
            data-testid="speed-dating-back-btn"
          >
            Browse Events
          </button>
        </div>
      </div>
    );
  }

  const totalRounds = event.total_rounds || 0;
  const roundLabel =
    event.status === "finished"
      ? "Event Finished"
      : `Round ${roundIndex + 1} of ${totalRounds}`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-slate-950 to-black text-white px-4 py-6 sm:px-6 sm:py-8">
      <MatchToast
        visible={matchToastVisible}
        partnerName={matchPartnerName}
        onClose={() => setMatchToastVisible(false)}
      />

      <div className="max-w-3xl mx-auto">
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-start gap-3">
            <button
              onClick={() => window.location.href = '/'}
              className="mt-1 p-2 rounded-full bg-white/10 hover:bg-white/20 transition"
              aria-label="Back to home"
              data-testid="speed-dating-back-btn"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
            </button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
                ⚡ {event.title || "Speed Dating"}
              </h1>
              <p className="text-xs sm:text-sm text-white/70 mt-1">
                Stay present, be kind, and follow the host&apos;s instructions.
              </p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <span className="text-xs sm:text-sm text-white/60" data-testid="speed-dating-round-label">
              {roundLabel}
            </span>
            {event.status === "running" && roundStartedAt && (
              <RoundTimer
                durationSeconds={event.round_duration_seconds || 180}
                startedAt={roundStartedAt}
              />
            )}
          </div>
        </header>

        {/* InlineNotice for consistent error/info display */}
        {notice && (
          <div className="mt-4" data-testid="speed-dating-notice-container">
            <InlineNotice
              variant={notice.variant}
              title={notice.title}
              actionLabel={notice.actionLabel}
              onAction={notice.onAction}
              href={notice.href}
              testId="speed-dating-notice"
            >
              {notice.message}
            </InlineNotice>
          </div>
        )}

        {/* Status message (informational, not error) */}
        {statusMessage && !notice && (
          <div 
            className="mt-4 rounded-2xl bg-white/5 border border-white/10 px-3 py-2 text-xs sm:text-sm text-white/80"
            data-testid="speed-dating-status"
          >
            {statusMessage}
          </div>
        )}

        <PartnerCard
          partner={currentPartner}
          onLike={handleLike}
          onPass={handlePass}
          disabled={likeInFlight || event.status !== "running"}
        />

        {/* More Ways to Join - expandable panel for Inner Circle & Special Events */}
        <div className="mt-4">
          <MoreWaysToJoin 
            isVerified={isVerified}
            isInnerCircle={isInnerCircle}
          />
        </div>

        {/* Video Date Button - only shown during active round with a partner */}
        {event.status === "running" && currentPartner && (
          <VideoDateButton
            eventId={event.id}
            roundIndex={roundIndex}
            partnerId={currentPartner.id}
            disabled={event.status !== "running"}
            onCallEnded={() => setStatusMessage("Video date ended. Make your choice below!")}
            onError={(err) => showError(err, { customMessage: "Video call failed. Please try again." })}
          />
        )}

        {event.status === "scheduled" && (
          <p className="mt-6 text-xs sm:text-sm text-white/60 text-center" data-testid="speed-dating-waiting">
            The host has not started this event yet. You&apos;ll automatically
            move into Round 1 when it begins.
          </p>
        )}

        {event.status === "finished" && (
          <p className="mt-6 text-xs sm:text-sm text-white/70 text-center" data-testid="speed-dating-finished">
            This event is complete. Any mutual likes will appear in your{" "}
            <span className="font-semibold">Matches</span> shortly.
          </p>
        )}
      </div>
    </div>
  );
}
